import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea
} from '@chakra-ui/react'
import moment from 'moment'
import isDateBeforeRentRollLockDate from 'lib/utils'
import { rentRollLedgerLockDate } from 'lib/config'
import axios from 'lib/axios'
import { BooksLockedNotice, useBuildiumPostingGuard } from 'lib/buildiumBooksLock'
import { isAllowedLiabilityType } from 'lib/liability-types'
import BuildiumPostingSummary from '../BuildiumPostingSummary'
import { RentalDetailsContext } from '../RentalDetailsContext'

type CreatedRentalLiability = { id: string }

type CreateRentalLiabilityFormProps = {
  onCancel: () => void
  onDirtyChange?: (isDirty: boolean) => void
  onSubmit: (createdLiabilityIds: string[]) => void | Promise<void>
  rentalId: string
  occupancyDate: string
}

CreateRentalLiabilityForm.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onDirtyChange: PropTypes.func,
  onSubmit: PropTypes.func,
  rentalId: PropTypes.string.isRequired
}

export default function CreateRentalLiabilityForm({
  onCancel,
  onDirtyChange,
  onSubmit = () => {},
  rentalId,
  occupancyDate
}: CreateRentalLiabilityFormProps) {
  const [initialDate] = useState(() => moment().format('YYYY-MM-DD'))
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState(initialDate)
  const [liabilityTypeId, setLiabilityTypeId] = useState('')
  const [note, setNote] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const { booksLock, isBooksLockLoading, isBooksLockError, isBuildiumBooksLocked } =
    useBuildiumPostingGuard({
      date,
      rentalId
    })

  const { liabilityTypes, rentalData } = useContext(RentalDetailsContext)

  const noteRequired =
    rentalData?.status === undefined || !['complete', 'canceled'].includes(rentalData.status)
  const numericAmount = Number(amount)
  const selectedLiabilityType = liabilityTypes.find(
    (liabilityType) => liabilityType.id === liabilityTypeId
  )
  const selectableLiabilityTypes = useMemo(
    () =>
      liabilityTypes
        .filter(isAllowedLiabilityType)
        .sort((left, right) => left.name.localeCompare(right.name)),
    [liabilityTypes]
  )
  const isDirty =
    amount !== '' || date !== initialDate || liabilityTypeId !== '' || note.trim().length > 0

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const amountError =
    amount !== '' && (!Number.isFinite(numericAmount) || numericAmount <= 0)
      ? 'Enter an amount greater than $0.00.'
      : null

  const getDateError = () => {
    if (!date) {
      return 'Date is required.'
    }

    if (!moment(date).isValid()) {
      return 'Enter a valid date.'
    }

    if (!occupancyDate || !moment(occupancyDate).isValid()) {
      return `The occupancy date ${
        occupancyDate || ''
      } is not valid. No liabilities can be created.`
    }

    if (!moment(date).isSameOrAfter(occupancyDate)) {
      return `Liability date must be on or after the occupancy date (${occupancyDate}).`
    }

    if (isDateBeforeRentRollLockDate(date)) {
      return `Updates to rent-roll before ${rentRollLedgerLockDate} are not allowed while FinOps is doing a Rent Roll Audit.`
    }

    return null
  }

  const dateError = getDateError()
  const isNoteMissing = noteRequired && note.trim().length === 0
  const canSubmit =
    !isSaving &&
    !amountError &&
    Number.isFinite(numericAmount) &&
    numericAmount > 0 &&
    !dateError &&
    Boolean(liabilityTypeId) &&
    !isNoteMissing &&
    !isBooksLockLoading &&
    !isBooksLockError &&
    !isBuildiumBooksLocked

  const handleLiabilityTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextLiabilityTypeId = event.target.value
    const nextLiabilityType = selectableLiabilityTypes.find(
      (liabilityType) => liabilityType.id === nextLiabilityTypeId
    )

    setLiabilityTypeId(nextLiabilityTypeId)
    if (nextLiabilityType?.price !== null && nextLiabilityType?.price !== undefined) {
      setAmount(String(nextLiabilityType.price))
    }
  }

  const saveLiability = useCallback(async () => {
    if (!canSubmit) {
      return
    }

    setIsSaving(true)
    setSaveError(null)

    try {
      const { data: createdLiabilities } = await axios.post<CreatedRentalLiability[]>(
        '/admin/rental_liabilities',
        {
          liabilities: [
            {
              amount: numericAmount,
              date,
              liability_type_id: liabilityTypeId,
              note: note.trim() || null,
              rental_id: rentalId
            }
          ]
        }
      )
      await onSubmit(createdLiabilities.map((liability) => liability.id))
    } catch (error: any) {
      setSaveError(error?.message || 'Unable to create rental liability')
    } finally {
      setIsSaving(false)
    }
  }, [canSubmit, date, liabilityTypeId, note, numericAmount, onSubmit, rentalId])

  return (
    <React.Suspense
      fallback={
        <Center py={12}>
          <Spinner />
        </Center>
      }
    >
      <Stack spacing={5}>
        {saveError && (
          <Alert status='error'>
            <AlertIcon />
            {saveError}
          </Alert>
        )}

        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }} gap={4}>
          <FormControl isRequired isInvalid={!liabilityTypeId && isDirty} isDisabled={isSaving}>
            <FormLabel>Liability type</FormLabel>
            <Select
              placeholder='Select a liability type'
              value={liabilityTypeId}
              onChange={handleLiabilityTypeChange}
            >
              {selectableLiabilityTypes.map((liabilityType) => (
                <option key={liabilityType.id} value={liabilityType.id}>
                  {liabilityType.name}
                </option>
              ))}
            </Select>
            <FormErrorMessage>Select a liability type.</FormErrorMessage>
          </FormControl>

          <FormControl
            isRequired
            isInvalid={Boolean(amountError)}
            isDisabled={isSaving || !selectedLiabilityType?.is_variable}
          >
            <FormLabel>Amount</FormLabel>
            <NumberInput min={0.01} precision={2} value={amount} onChange={setAmount}>
              <NumberInputField placeholder='0.00' />
            </NumberInput>
            {amountError ? (
              <FormErrorMessage>{amountError}</FormErrorMessage>
            ) : selectedLiabilityType && !selectedLiabilityType.is_variable ? (
              <FormHelperText>Fixed by the selected liability type.</FormHelperText>
            ) : null}
          </FormControl>

          <FormControl
            isRequired
            isInvalid={Boolean(dateError) || isBooksLockError || isBuildiumBooksLocked}
            isDisabled={isSaving}
          >
            <FormLabel>Date</FormLabel>
            <Input type='date' value={date} onChange={(event) => setDate(event.target.value)} />
            {dateError && <FormErrorMessage>{dateError}</FormErrorMessage>}
          </FormControl>

          <BuildiumPostingSummary
            amount={numericAmount}
            date={date}
            dateError={dateError}
            entryDescription='One rental liability'
            isBooksLockError={isBooksLockError}
            isBooksLockLoading={isBooksLockLoading}
            isBuildiumBooksLocked={isBuildiumBooksLocked}
            lockDate={booksLock?.lockDate}
            typeLabel={selectedLiabilityType?.name}
          />
        </Grid>

        {isBooksLockLoading && <Text fontSize='sm'>Checking Buildium books-lock status...</Text>}
        {(isBooksLockError || isBuildiumBooksLocked) && (
          <BooksLockedNotice lockDate={booksLock?.lockDate} isError={isBooksLockError} />
        )}

        <FormControl
          isRequired={noteRequired}
          isInvalid={isNoteMissing && isDirty}
          isDisabled={isSaving}
        >
          <FormLabel>Note</FormLabel>
          <Textarea
            minH='120px'
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder='Add the reason for this liability'
          />
          {noteRequired ? (
            <FormErrorMessage>Enter a note for this liability.</FormErrorMessage>
          ) : (
            <FormHelperText>Optional for completed or canceled rentals.</FormHelperText>
          )}
        </FormControl>

        <Divider />

        <Box display='flex' justifyContent='flex-end' gap={3}>
          <Button variant='outline' onClick={onCancel} isDisabled={isSaving}>
            Cancel
          </Button>
          <Button
            colorScheme='teal'
            onClick={saveLiability}
            isDisabled={!canSubmit}
            isLoading={isSaving}
          >
            Create Liability
          </Button>
        </Box>
      </Stack>
    </React.Suspense>
  )
}
